import { Download, FileUp } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { ImportKind, type ImportJob } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Progress } from '@nucleus-commerce/ui/components/progress';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCreateImport } from '../hooks/use-create-import';
import { useDownloadSample } from '../hooks/use-download-sample';
import { useTrackedImport } from '../hooks/use-tracked-import';
import { missingColumnLabels } from '../schemas/column-guide';
import { importFileProblem } from '../schemas/import-rules';
import {
  importFormSchema,
  importOnExisting,
  type ImportFormInput,
  type ImportFormValues,
} from '../schemas/job-forms';
import type { TrackedJob } from '../schemas/job-rules';

import { ColumnGuideList } from './column-guide-list';
import { TrackedJobStatus } from './tracked-job-status';

export interface ImportFormCardProps {
  storeId: string;
  defaultKind: ImportKind | undefined;
  onOpenJob: (job: ImportJob) => void;
}

/** Uploads a CSV as an import job and follows it; file refusals land on the file field. */
export function ImportFormCard({ storeId, defaultKind, onOpenJob }: ImportFormCardProps) {
  const { t } = useTranslation('importsExports');
  const format = useFormat();
  const message = useErrorMessage();
  const create = useCreateImport(storeId);
  const sample = useDownloadSample(storeId);
  const [tracked, setTracked] = useState<TrackedJob | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const tracker = useTrackedImport(storeId, tracked);

  const form = useForm<ImportFormInput, unknown, ImportFormValues>({
    resolver: useI18nResolver(importFormSchema),
    defaultValues: { kind: defaultKind ?? 'products_csv', onExisting: 'skip', file: null },
  });
  const kind = useWatch({ control: form.control, name: 'kind' });

  const onSubmit = form.handleSubmit((values) => {
    if (!values.file) return;
    create.mutate(
      { kind: values.kind, file: values.file, onExisting: importOnExisting(values) },
      {
        onSuccess: (job) => {
          setTracked({ id: job.id, startedAt: Date.now() });
          form.reset({ kind: values.kind, onExisting: values.onExisting, file: null });
          setInputKey((k) => k + 1);
        },
        onError: (error) => {
          const problem = importFileProblem(error);
          if (!problem) {
            toast.error(message(error));
            return;
          }
          const text =
            problem.kind === 'missingColumns'
              ? t('import.problems.missingColumns', {
                  count: problem.missing.length,
                  columns: missingColumnLabels(values.kind, problem.missing, t('guide.or')).join(
                    ', ',
                  ),
                })
              : t(`import.problems.${problem.kind}`);
          form.setError('file', { type: 'server', message: text }, { shouldFocus: true });
        },
      },
    );
  });

  const job = tracker.job;
  const percent = job && job.totalRows > 0 ? (job.processedRows / job.totalRows) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[15px]">{t('import.title')}</CardTitle>
        <CardDescription>{t('import.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Form {...form}>
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('import.kind')}</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      form.clearErrors('file');
                    }}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(ImportKind).map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`kinds.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {kind === 'products_csv' ? (
              <FormField
                control={form.control}
                name="onExisting"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('import.onExisting.label')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-col gap-2"
                      >
                        {(['skip', 'update'] as const).map((option) => (
                          <FormItem key={option} className="flex flex-row items-start gap-2">
                            <FormControl>
                              <RadioGroupItem value={option} className="mt-0.5" />
                            </FormControl>
                            <div className="flex flex-col gap-0.5">
                              <FormLabel className="font-normal">
                                {t(`import.onExisting.${option}`)}
                              </FormLabel>
                              {option === 'update' ? (
                                <span className="text-muted-foreground text-xs">
                                  {t('import.onExisting.updateHint')}
                                </span>
                              ) : null}
                            </div>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <FormField
              control={form.control}
              name="file"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('import.file')}</FormLabel>
                  <FormControl>
                    <Input
                      key={inputKey}
                      type="file"
                      accept=".csv,text/csv"
                      name={field.name}
                      ref={field.ref}
                      onBlur={field.onBlur}
                      onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
                    />
                  </FormControl>
                  <FormDescription>{t('import.fileHint')}</FormDescription>
                  <FormMessage />
                  <ColumnGuideList kind={kind} />
                  <div className="flex flex-col items-start gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={sample.isPending}
                      onClick={() => sample.mutate(kind)}
                    >
                      <Download aria-hidden />
                      {t('import.sample')}
                    </Button>
                    <p className="text-muted-foreground text-xs">{t('import.sampleHint')}</p>
                  </div>
                </FormItem>
              )}
            />
            <Button type="submit" disabled={create.isPending} className="self-start">
              <FileUp aria-hidden />
              {create.isPending ? t('import.uploading') : t('import.start')}
            </Button>
          </form>
        </Form>
        {job ? (
          <TrackedJobStatus
            tracker={tracker}
            title={t('import.latest', { kind: t(`kinds.${job.kind}`) })}
          >
            <Progress value={percent} aria-label={t('columns.processed')} />
            <p className="text-muted-foreground text-xs">
              {t('progress', {
                processed: format.number(job.processedRows),
                total: format.number(job.totalRows),
              })}
              {job.errorRows > 0 ? ` · ${t('import.errorRows', { count: job.errorRows })}` : ''}
            </p>
            {job.status === 'completed' || job.status === 'failed' ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="self-start"
                onClick={() => onOpenJob(job)}
              >
                {job.errorRows > 0 ? t('import.viewErrors') : t('details.open')}
              </Button>
            ) : null}
          </TrackedJobStatus>
        ) : null}
      </CardContent>
    </Card>
  );
}
